// @flow
import React, { PureComponent } from "react";
import { connect, Dispatch } from "react-redux";
import SearchForm from "components/SearchForm";
import Status from "components/Status";
import { getSearchFormAction, setSearchFormChangesAction } from "data/actions";
import {
    type SearchFormResultDTO,
    type SearchItemDTO,
    type SearchFilterDTO,
    SearchItemType,
    FormLookupValue,
} from "data/types";
import Constants, {
    isFeatureOn,
    Feature,
    type ConstantsEnum,
} from "data/constants";
import immutable from "object-path-immutable";
import * as s from "data/reducers/selectors";
import SearchFormHeader from "components/SearchFormHeader";

type Props = {
    /** search form id to render */
    id: number,
    /** optional extra filters to set on searchRequest */
    extraFilters?: Array<SearchFilterDTO>,
    /** current itemUri */
    itemUri: string,
    /** label to show on Search button */
    searchLabel: string,
    /** callback when user clicks the Search button
     * @param {SearchItemDTO} searchItem searchItem containing query
     */
    onSearch: (searchItem: SearchItemDTO) => void,
    /** callback when user clicks the Cancel button */
    onCancel: () => void,
    /** Redux dispatch */
    dispatch: Dispatch,
    /** the search form itself */
    form: ?SearchFormResultDTO,
    /** search form fetch status */
    status: ConstantsEnum,
    /** whether to enable the search button */
    searchEnabled?: boolean,
    /** whether to show search operators */
    showSearchOperators?: boolean,
};

type State = {
    changed: Object,
    form: ?SearchFormResultDTO,
};

export class SearchFormContainer extends PureComponent<Props, State> {
    state = {
        changed: {},
        form: undefined,
    };

    static defaultProps = {
        searchEnabled: true,
        showSearchOperators: false,
    };

    componentDidMount = () => this._fetch(this.props.id);

    componentDidUpdate(prevProps: Props) {
        /* istanbul ignore else */
        if (
            prevProps.itemUri !== this.props.itemUri ||
            prevProps.id !== this.props.id
        ) {
            this._fetch(this.props.id);
        }
        /* istanbul ignore else */
        if (
            prevProps.form !== this.props.form &&
            this.props.form != null &&
            this.props.form.attributes != null
        ) {
            const changed = {};
            const attributes = [];
            // create copy of attributes
            this.props.form.attributes.forEach((attr, i) => {
                let val = attr.value;
                let op = attr.op;
                // prefill free text search with value from store...
                if (
                    (prevProps.form == null ||
                        prevProps.form.name === this.props.form.name ||
                        attr.name === "$SEARCHTEXT$") &&
                    this.props.changes[attr.name]
                ) {
                    val = this.props.changes[attr.name].val;
                    op = this.props.changes[attr.name].op;
                }
                attributes[i] = immutable.set(attr, "op", op);
                attributes[i] = immutable.set(attributes[i], "value", val);
                changed[attr.name] = {
                    val,
                    op,
                };
            });
            // apply extraFilters
            if (
                this.props.extraFilters != null &&
                this.props.extraFilters.length > 0
            ) {
                let customId = -50;
                this.props.extraFilters.forEach((filter) => {
                    // add to form definition
                    attributes.splice(0, 0, {
                        hidden: true,
                        id: customId--,
                        header: filter.name,
                        name: filter.name,
                        value: filter.value,
                        valueType: filter.valueType,
                        op: filter.op,
                        nodeType: filter.nodeType,
                    });
                    // add values
                    changed[filter.name] = {
                        val: filter.value,
                        op: filter.op,
                    };
                });
            }
            let form = immutable.set(this.props.form, "attributes", attributes);
            form = immutable.set(form, "name", this.props.name);
            this.setState(
                {
                    changed,
                    form,
                },
                () => {
                    this.props.dispatch(setSearchFormChangesAction(changed));
                    if (isFeatureOn(Feature.searchAsYouType)) this._onSearch();
                }
            );
        }
    }

    _fetch = (id: number): void => {
        this.props.dispatch(
            getSearchFormAction({
                id,
                model: this.state.changed,
            })
        );
    };

    _hasChanged = () =>
        this.state?.form?.attributes.filter((attr, ind) =>
        (this.props.form?.attributes[ind]?.value !== attr.value &&
            !(this.props.form?.attributes[ind]?.value === null && attr?.value === ''))
        )?.length > 0

    _onValidated = (isValid: boolean) => {
        if (isValid && isFeatureOn(Feature.searchAsYouType)) this._onSearch();
    };

    _onChange = (name: string, value: any) => {
        const splittedName = name.split(".");
        if (
            value == null &&
            !Object.prototype.hasOwnProperty.call(
                this.state.changed,
                splittedName[1]
            )
        ) {
            return;
        }
        const oldValue = this.state.changed[splittedName[1]];
        // console.log("containers/searchForm onChange", name, oldValue, value);
        if (
            oldValue != null &&
            value != null &&
            (oldValue.val !== value.val || oldValue.op !== value.op)
        ) {
            // console.log("containers/searchForm doChange!");
            const idx = this.state.form.attributes.findIndex(
                (a) => a.name === splittedName[1]
            );
            // set operator
            let form = immutable.set(
                this.state.form,
                ["attributes", idx, "op"],
                value.op
            );
            // set value
            form = immutable.set(form, ["attributes", idx, "value"], value.val);

            const changed = immutable.set(
                this.state.changed,
                splittedName[1],
                value
            );
            // update local state
            this.setState(
                {
                    changed,
                    form,
                },
                () => {
                    this.props.dispatch(setSearchFormChangesAction(changed));
                }
            );
        }
    };

    _clear = () => {
        let form = immutable.set(this.state.form, "attributes", this.props.form.attributes);
        let changed = this.props.form.attributes?.reduce((prev, curr) =>
            ({ ...prev, [curr.name]: { val: curr.value, op: curr.op } }),
            {});
        this.setState(
            {
                changed,
                form,
            },
            () => {
                this.props.dispatch(setSearchFormChangesAction(changed));
                //if (isFeatureOn(Feature.searchAsYouType)) this._onSearch();
            }
        );
    }

    _toFilterValue = (value: string | FormLookupValue) =>
        value && (value.T || value);

    // send our search model with searchOperators
    _onSearch = () => {
        const { changed, form } = this.state;
        // console.log("searchForm", searchForm);
        // console.log("model", model);

        // convert model into filters
        const filters = [];
        let searchText = null;
        form.attributes.forEach((attr) => {
            const changedField = changed[attr.name];
            /* istanbul ignore else */
            if (changedField != null) {
                filters.push({
                    name: attr.name,
                    op: changedField.op,
                    value: this._toFilterValue(changedField.val),
                    valueType: attr.valueType,
                    nodeType: attr.nodeType,
                    keywordField: attr.keywordField,
                });
                // keep special searchText for highlighting
                if (attr.name === "$SEARCHTEXT$")
                    searchText = this._toFilterValue(changedField.val);
            }
        });

        // build searchItem
        const searchItem: SearchItemDTO = {
            id: this.props.id,
            name: this.props.form.name,
            searchType: SearchItemType.Form,
            searchText,
            filters,
            sorts: this.props.form.sorts,
        };

        /* istanbul ignore else */
        if (typeof this.props.onSearch === "function")
            this.props.onSearch(searchItem);
    };

    render = () => (
        <>
            <SearchFormHeader
                searchForm={this.props.form}
                onCancel={this.props.onCancel}
                clearEnabled={this._hasChanged}
                onClear={this._clear}
            />
            {this.props.status !== Constants.OK && (
                <Status status={this.props.status} />
            )}
            {this.props.status === Constants.OK && this.state.form == null && (
                <Status status={Constants.ERROR} />
            )}
            {this.props.status === Constants.OK && this.state.form != null && (
                <SearchForm
                    key={`sf-${this.props.id}`}
                    itemUri={this.props.itemUri}
                    form={this.state.form}
                    searchLabel={this.props.searchLabel}
                    searchEnabled={this.props.searchEnabled}
                    showSearchOperators={this.props.showSearchOperators}
                    onCancel={this.props.onCancel}
                    onSearch={this._onSearch}
                    onChange={this._onChange}
                    onValidated={this._onValidated}
                />
            )}
        </>
    );
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    status: s.searchFormStatusSelector(state),
    form: s.searchFormResultSelector(state),
    showSearchOperators: s.settingValueSelector("showSearchOperators", state),
    changes: s.searchFormChangesSelector(state),
    ...ownProps,
});

export default connect(mapStateToProps)(SearchFormContainer);
